"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/features/auth/context/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { getProfileTableFromUserRole } from "@/lib/auth-utils";
import { supabase } from "@/lib/supabase";
import React, { useState } from "react";

export default function ProfileSettingsPage() {
  const { profile: userProfile } = useAuth();
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    email: userProfile.email,
    givenName: userProfile.given_name || "",
    familyName: userProfile.family_name || ""
  });
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setSaving(true);
    try {
      // Update email if changed
      if (formData.email !== userProfile.email) {
        const { error: emailError } = await supabase.auth.updateUser({
          email: formData.email
        });
        if (emailError) throw emailError;
      }

      // Update profile name fields if changed
      if (
        formData.givenName !== userProfile.given_name ||
        formData.familyName !== userProfile.family_name
      ) {
        const { error: profileError } = await supabase
          .from(getProfileTableFromUserRole(userProfile.role)!)
          .update({
            given_name: formData.givenName,
            family_name: formData.familyName
          })
          .eq("id", userProfile.id);
        if (profileError) throw profileError;
        toast({
          title: "Erfolg",
          description: "Dein Profil wurde erfolgreich aktualisiert."
        });
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      toast({
        title: "Fehler",
        description:
          "Fehler beim Aktualisieren deines Profils. Bitte versuche es erneut.",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Profil</h2>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <label htmlFor="email" className="text-sm font-medium">
            E-Mail
          </label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, email: e.target.value }))
            }
          />
          <p className="text-sm text-gray-500">
            Deine E-Mail-Adresse wird für die Anmeldung und Benachrichtigungen
            verwendet.
          </p>
        </div>

        <div className="space-y-2">
          <label htmlFor="givenName" className="text-sm font-medium">
            Vorname
          </label>
          <Input
            id="givenName"
            type="text"
            value={formData.givenName}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, givenName: e.target.value }))
            }
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="familyName" className="text-sm font-medium">
            Nachname
          </label>
          <Input
            id="familyName"
            type="text"
            value={formData.familyName}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, familyName: e.target.value }))
            }
          />
        </div>
      </div>

      <Button type="submit" disabled={saving}>
        {saving ? "Speichern..." : "Profil aktualisieren"}
      </Button>
    </form>
  );
}
